import {
  IAircraftMaintenance,
  IMaintenanceCounter,
  IMaintenanceItem,
  IMaintenanceItemComplete,
  MaintenanceItemType,
} from "@vmosolutions/oc-interfaces";

/**
 * The categorised maintenance item arrays used by both the tab and the badge counters.
 */
export interface ICategorisedMaintenanceItems {
  overdue: IMaintenanceItem[];
  nextDue: IMaintenanceItem[];
  deferred: IMaintenanceItem[];
  completed: IMaintenanceItemComplete[];
}

/**
 * Holds the counts for overdue, due soon, and deferred maintenance items for an aircraft.
 */
export interface IMaintenanceItemCounts {
  overdue: number;
  dueSoon: number;
  deferred: number;
}

/**
 * Composite key for uniquely identifying a maintenance item across categories.
 */
export const getMaintenanceItemKey = (item: IMaintenanceItem): string =>
  `${item.itemId}:${item.component ?? ""}:${item.itemType}`;

/** Safely parses a date that may arrive as an ISO string from JSON deserialization. */
const toDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Determines the due date of a maintenance item for sorting and categorisation purposes.
 * If the item is already complete, this instead returns the completed date.
 *
 * @todo (DEV-782): Value-based overdue calculation, using counter.dueAtValue
 *
 * @param item The maintenance item to evaluate
 * @returns The due date for active items, or completed date for completed items. Null if no date information is available.
 */
export const getMaintenanceItemDueDate = (item: IMaintenanceItem): Date | null => {
  const reduceCounterDue = (soonest: Date | null, counter: IMaintenanceCounter): Date | null => {
    const dueDate = toDate(counter.dueAtDate);
    if (dueDate) {
      return !soonest || dueDate < soonest ? dueDate : soonest;
    }

    return soonest;
  };

  switch (item.itemType) {
    case MaintenanceItemType.Counter:
      return item.counters.reduce<Date | null>(reduceCounterDue, null);

    case MaintenanceItemType.Deferred:
      return toDate(item.mel.dueDate);

    case MaintenanceItemType.Inspection:
      return toDate(item.projectedDueDate);

    case MaintenanceItemType.Complete:
      return toDate(item.completedDate);

    default:
      return null;
  }
};

export const sortMaintenanceItemsByDueDate = (a: IMaintenanceItem, b: IMaintenanceItem): number => {
  const aDue = getMaintenanceItemDueDate(a);
  const bDue = getMaintenanceItemDueDate(b);
  if (!aDue) return 1;
  if (!bDue) return -1;
  return aDue.getTime() - bDue.getTime();
};

/**
 * Determines whether a maintenance item's due date has passed.
 */
export const isMaintenanceItemOverdue = (item: IMaintenanceItem): boolean => {
  if (item.itemType === MaintenanceItemType.Complete) return false;

  const now = new Date();
  const dueDate: Date | null = getMaintenanceItemDueDate(item);
  return dueDate?.valueOf() < now.valueOf();
};

/**
 * Splits maintenance items into display categories: overdue, next due, deferred, completed.
 * Overdue items are removed from their home category so they only appear once.
 */
export const getCategorisedMaintenanceItems = (
  maintenance: IAircraftMaintenance | undefined
): ICategorisedMaintenanceItems => {
  const empty: ICategorisedMaintenanceItems = { overdue: [], nextDue: [], deferred: [], completed: [] };
  if (!maintenance?.maintenanceItems) return empty;

  const items = maintenance.maintenanceItems;

  const allActive: IMaintenanceItem[] = [
    ...(items.counterItems ?? []),
    ...(items.inspections ?? []),
    ...(items.deferred ?? []),
  ];

  const overdue = allActive.filter(isMaintenanceItemOverdue).sort(sortMaintenanceItemsByDueDate);
  const overdueKeys = new Set(overdue.map(getMaintenanceItemKey));

  const nextDue: IMaintenanceItem[] = [...(items.counterItems ?? []), ...(items.inspections ?? [])]
    .filter((i) => !overdueKeys.has(getMaintenanceItemKey(i)))
    .sort(sortMaintenanceItemsByDueDate);

  const deferred = (items.deferred ?? [])
    .filter((i) => !overdueKeys.has(getMaintenanceItemKey(i)))
    .sort(sortMaintenanceItemsByDueDate);

  const completed = (items.completed ?? []).sort(sortMaintenanceItemsByDueDate);

  return { overdue, nextDue, deferred, completed };
};

/**
 * Returns just the counts from the categorised arrays.
 */
export const getMaintenanceItemCounts = (maintenance: IAircraftMaintenance | undefined): IMaintenanceItemCounts => {
  const { overdue, nextDue, deferred } = getCategorisedMaintenanceItems(maintenance);
  return {
    overdue: overdue.length,
    dueSoon: nextDue.length,
    deferred: deferred.length,
  };
};
